import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Building, Check, ChevronLeft, ChevronRight, CircleCheck, CircleMinus, Copy, ExternalLink, FileSearch, FlaskConical, Package, Plus, Receipt, ReceiptText,
  Repeat, Send, Trash2, UserRound, Users, X,
} from 'lucide-react';
import {
  closeCustomerProject, createCustomerInvoice, createCustomerSubmission, loadCustomer, loadCustomerSubmission, loadOrganization, markCustomerInvoicePaid,
  openCustomerDocument, openCustomerFile, saveBillingProfile, saveCustomerDetails, saveOrganization, sendCustomerLink, withdrawCustomerSubmission,
} from './api';
import { labError, labWhen } from './LaboratoryParts';
import { QC_TONES, fileSize, qcResult } from './qcResults';
import { Empty, Failed, Loading, Pill, WorkspaceTabs, XomicsSelect } from './parts';
import { Switch } from './ProductOptions';
import { answerText, filledColumns } from './sampleAnswers';
import { Field, fieldAria } from './ui';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import { useMoneyTotal } from './viewMoney';
import './productDetail.css';
import './organizations.css';

// A customer, as the Customers page shows one in place of its list. Everything we do with a
// customer is a project: we ask them for samples of one workflow at a price for each, with other
// costs beside them when there are some. A one-time project is for a number of samples, sent once
// (Requested); one of their people sends the samples (Submitted); the laboratory works them (In the
// lab) until every one is done (Done). A continuous project is for a partner who sends samples now
// and then: it is open, each box they send a submission of its own with its submission form, until
// we close it. We can withdraw a project before anything is sent for it. Its invoices are attached
// to it: the first made with a one-time project when we want, more later (a continuous one is
// invoiced as its samples come). Each is a pro forma invoice (a preview of the bill for them to look
// over: no due date, nothing paid on it) or an invoice (the bill they pay by its due date, marked
// paid by us); only invoices count as billed. A project opens as a sheet over the page, with its
// overview, its submissions (each with its samples, and whether the customer asked for patient
// reports on them: we see where the reports are, never the people) and its invoices. Beside the
// projects: all their invoices, the accounts in their portal, and their details. A new project, a
// new account and changing the organization are forms over the page.
const text = (key, values) => tx(`ui.customers.${key}`, values);
const counted = (key, count) => text(count === 1 ? `${key}_one` : key, { count });
const org = (key, values) => tx(`ui.org.${key}`, values);
const TABS = Object.freeze({ projects: FlaskConical, invoices: Receipt, accounts: Users, details: Building });
const STAGES = ['requested', 'sent', 'in_lab', 'done'];
// A submission of a continuous project: its box sent, its samples in the laboratory, all done.
const BOX_STAGES = ['sent', 'in_lab', 'done'];
const STAGE_TONES = { requested: 'warn', sent: 'info', in_lab: 'info', done: 'ok', withdrawn: 'flat', open: 'ok', closed: 'flat' };
const REPORT_TONES = { making: 'info', review: 'warn', ready: 'ok', held: 'bad', failed: 'bad' };
const INVOICE_TONES = { due: 'warn', overdue: 'bad', paid: 'ok', proforma: 'info' };
const DAY_MS = 24 * 60 * 60 * 1000;
// An invoice still to be paid: due, or overdue. A pro forma never is.
const toPay = (invoice) => invoice.status === 'due' || invoice.status === 'overdue';
const SAMPLE_TONES = { waiting: 'flat', received: 'info', in_progress: 'info', completed: 'ok', stopped: 'bad' };
const EMPTY_ORGANIZATION = { code: '', name: '', typeCode: '', countryCode: '', contactName: '', contactEmail: '', contactPhone: '', isActive: true };

const day = (value) => new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(value));
const shortDay = (value) => new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' }).format(new Date(value));
function money(minor, currency, minorUnit) {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, minimumFractionDigits: minorUnit, maximumFractionDigits: minorUnit })
      .format(Number(minor) / 10 ** minorUnit);
  } catch (_error) {
    return `${(Number(minor) / 10 ** minorUnit).toFixed(minorUnit)} ${currency}`;
  }
}
function symbolOf(currency) {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' })
      .formatToParts(0).find((part) => part.type === 'currency')?.value ?? currency;
  } catch (_error) {
    return currency;
  }
}
// A typed amount in the currency's smallest unit (cents), or null when it is not one. "3,600",
// "3600.00" and "3.600,00" all read as three thousand six hundred: of a comma and a point the last
// one is the decimal mark, and a comma before groups of three digits only separates thousands.
function toMinor(value, minorUnit) {
  let clean = String(value ?? '').trim().replace(/[\s']/g, '');
  if (clean.includes(',') && clean.includes('.')) {
    clean = clean.lastIndexOf(',') > clean.lastIndexOf('.') ? clean.replace(/\./g, '').replace(',', '.') : clean.replace(/,/g, '');
  } else if (/^\d{1,3}(,\d{3})+$/.test(clean)) {
    clean = clean.replace(/,/g, '');
  } else {
    clean = clean.replace(',', '.');
  }
  if (!/^\d+(\.\d+)?$/.test(clean)) return null;
  const [whole, fraction = ''] = clean.split('.');
  if (fraction.length > minorUnit) return null;
  return Number(whole) * 10 ** minorUnit + Number(fraction.padEnd(minorUnit, '0') || 0);
}
const fromMinor = (minor, minorUnit) => (Number(minor) / 10 ** minorUnit).toFixed(minorUnit);
const initials = (name) => String(name || '').split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word[0].toUpperCase()).join('');

// Who of theirs heard of something new (a submission and its invoice, another invoice): everyone
// with an account was emailed, or nobody has one yet.
function toldLine(made, customerName) {
  if (made.accounts === 0) return text('told_no_accounts', { name: customerName });
  if (made.emailed.length === 0) return text('told_not_emailed');
  return text('told_emailed', { count: made.emailed.length, names: made.emailed.join(', ') });
}

function useBusy() {
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const run = async (work) => {
    setBusy(true); setError('');
    try { return await work(); } catch (caught) { setError(labError(caught)); return null; } finally { setBusy(false); }
  };
  return { busy, error, run, setError };
}

const openDocument = (kind, uuid) => openCustomerDocument(kind, uuid).catch(() => {});

// Escape closes what is open over the page, unless it is busy.
function useEscape(onClose, busy = false) {
  useEffect(() => {
    const close = (event) => { if (event.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose, busy]);
}

// A form over the page, as the portal's other forms are (a new product, a shipment): its title,
// its sections, and a footer with what saving does and its buttons.
function FormModal({ id, title, subtitle, busy, onClose, onSubmit, note, submitLabel, submitDisabled, width = 'default', cancel = true, children }) {
  useEscape(onClose, busy);
  return <div className="xo-product-modal-backdrop" role="presentation" onMouseDown={() => { if (!busy) onClose(); }}>
    <section className={`xo-product-editor xo-cust-editor is-${width}`} role="dialog" aria-modal="true" aria-labelledby={`${id}-title`}
      onMouseDown={(event) => event.stopPropagation()}>
      <header>
        <div><h2 id={`${id}-title`}>{title}</h2>{subtitle && <p>{subtitle}</p>}</div>
        <button type="button" className="xo-icon-btn" aria-label={text('close')} disabled={busy} onClick={onClose}><X aria-hidden="true" /></button>
      </header>
      <form className="xo-product-editor-form" onSubmit={onSubmit} noValidate>
        <div className="xo-product-editor-scroll">{children}</div>
        <footer>
          <span>{note}</span>
          <div>
            {cancel && <button type="button" className="xo-btn-2" disabled={busy} onClick={onClose}>{text('cancel')}</button>}
            {submitLabel && <button type="submit" className="xo-btn" disabled={busy || submitDisabled}>{submitLabel}</button>}
          </div>
        </footer>
      </form>
    </section>
  </div>;
}

function FormSection({ number, title, detail, children }) {
  return <section className="xo-product-form-section">
    <div className="xo-product-form-heading">{number && <span>{number}</span>}<div><h3>{title}</h3>{detail && <p>{detail}</p>}</div></div>
    {children}
  </section>;
}

// An amount typed in a currency: its symbol before, its code after.
function MoneyInput({ id, value, onChange, onBlur, currency, disabled, describedBy }) {
  return <div className="xo-product-money-control">
    <span>{symbolOf(currency)}</span>
    <input id={id} inputMode="decimal" value={value} onChange={(event) => onChange(event.target.value)} onBlur={onBlur} disabled={disabled}
      aria-describedby={describedBy} autoComplete="off" />
    <strong>{currency}</strong>
  </div>;
}

// Other costs beside the samples: a line of text and an amount each, as many as needed.
function CostLines({ idPrefix, costs, setCosts, currency, disabled }) {
  const change = (key, field) => (value) => setCosts((current) => current.map((cost) => (cost.key === key ? { ...cost, [field]: value } : cost)));
  return <div className="xo-cust-costs">
    {costs.map((cost, index) => <div className="xo-cust-cost" key={cost.key}>
      <Field id={`${idPrefix}-cost-${cost.key}`} label={index === 0 ? text('cost_description') : ''}>
        <input {...fieldAria(`${idPrefix}-cost-${cost.key}`)} value={cost.description} maxLength={255} disabled={disabled}
          placeholder={text('cost_placeholder')} onChange={(event) => change(cost.key, 'description')(event.target.value)} />
      </Field>
      <Field id={`${idPrefix}-amount-${cost.key}`} label={index === 0 ? text('cost_amount') : ''}>
        <MoneyInput id={`${idPrefix}-amount-${cost.key}`} value={cost.amount} onChange={change(cost.key, 'amount')} currency={currency} disabled={disabled} />
      </Field>
      <button type="button" className="xo-icon-btn" aria-label={text('remove_cost')} disabled={disabled}
        onClick={() => setCosts((current) => current.filter((entry) => entry.key !== cost.key))}><Trash2 aria-hidden="true" /></button>
    </div>)}
    <button type="button" className="xo-btn-2 xo-cust-add-line" disabled={disabled || costs.length >= 20}
      onClick={() => setCosts((current) => [...current, { key: `${Date.now()}-${current.length}`, description: '', amount: '' }])}>
      <Plus aria-hidden="true" />{text('add_line')}</button>
  </div>;
}

// Costs as the API takes them, or null while one is not filled in right.
function costsOf(costs, minorUnit) {
  const read = costs.map((cost) => ({ description: cost.description.trim(), amountMinor: toMinor(cost.amount, minorUnit) }));
  return read.every((cost) => cost.description !== '' && cost.amountMinor !== null) ? read : null;
}

// A choice that is on or off, with the portal's own switch; what it opens goes under it.
function Choice({ title, detail, on, onChange, disabled, children }) {
  return <div className={`xo-cust-option${on ? ' is-on' : ''}`}>
    <div className="xo-cust-option-head">
      <span><strong>{title}</strong>{detail && <small>{detail}</small>}</span>
      <Switch on={on} onChange={onChange} label={title} disabled={disabled} />
    </div>
    {children}
  </div>;
}

// The two kinds of invoice side by side, each with what it means: a pro forma for them to look over
// (what it leaves out), or the invoice they pay (what it carries).
const KINDS = Object.freeze([
  { kind: 'proforma', Icon: FileSearch, Fact: CircleMinus },
  { kind: 'invoice', Icon: ReceiptText, Fact: CircleCheck },
]);

function InvoiceKinds({ value, onChange, disabled }) {
  return <div className="xo-cust-kinds" role="radiogroup" aria-label={text('kind_title')}>
    {KINDS.map(({ kind, Icon, Fact }) => <button type="button" key={kind} role="radio" aria-checked={value === kind} disabled={disabled}
      className={`xo-cust-kind is-${kind}${value === kind ? ' is-on' : ''}`} onClick={() => onChange(kind)}>
      <span className="xo-cust-kind-head">
        <span className="xo-cust-kind-art" aria-hidden="true"><Icon /></span>
        <span className="xo-cust-kind-tick" aria-hidden="true">{value === kind && <Check />}</span>
      </span>
      <strong>{text(`kind.${kind}`)}</strong>
      <small>{text(`kind.${kind}_line`)}</small>
      <ul>{[1, 2, 3].map((n) => <li key={n}><Fact aria-hidden="true" />{text(`kind.${kind}_fact_${n}`)}</li>)}</ul>
    </button>)}
  </div>;
}

// Under the kind chosen: in how many days an invoice is due (and the day that comes to), or, for a
// pro forma, that it has no due date.
function DueTerms({ id, kind, dueDays, setDueDays, disabled }) {
  if (kind === 'proforma') return <p className="xo-cust-kind-note">{text('kind_proforma_project_note')}</p>;
  const days = Number(dueDays);
  const known = dueDays !== '' && Number.isInteger(days) && days >= 0 && days <= 180;
  return <div className="xo-cust-due"><Field id={id} label={text('due_in_days')} hint={known ? text('due_on_day', { date: day(Date.now() + days * DAY_MS) }) : ''}>
    <input {...fieldAria(id, { hint: known })} type="number" min={0} max={180} value={dueDays} onChange={(event) => setDueDays(event.target.value)} disabled={disabled} />
  </Field></div>;
}

// What it comes to: each line, then the total. A line of nothing is left out.
function Totals({ lines, totalMinor, currency, minorUnit, totalLabel }) {
  return <dl className="xo-cust-totals">
    {lines.filter((line) => line.minor !== 0).map((line) => <div key={line.key}><dt>{line.label}</dt><dd>{line.minor === null ? '—' : money(line.minor, currency, minorUnit)}</dd></div>)}
    <div className="is-total"><dt>{totalLabel}</dt><dd>{totalMinor === null ? '—' : money(totalMinor, currency, minorUnit)}</dd></div>
  </dl>;
}

// ------------------------------------------------------------------------------- the organization

// The organization itself: made with "Add customer", changed under Details.
export function OrganizationForm({ organization, types, onSaved, onCancel }) {
  const [fields, setFields] = useState(organization === null ? EMPTY_ORGANIZATION : {
    code: organization.code, name: organization.name, typeCode: organization.type.code, countryCode: organization.countryCode,
    contactName: organization.contact.name, contactEmail: organization.contact.email, contactPhone: organization.contact.phone || '',
    isActive: organization.isActive });
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const inFlight = useRef(false);
  const set = (key) => (event) => setFields((current) => ({ ...current, [key]: event.target.value }));
  async function submit(event) {
    event.preventDefault();
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setError('');
    const { code, ...rest } = fields;
    try {
      const saved = await saveOrganization(organization === null ? null : organization.id,
        { ...(organization === null ? { code } : {}), ...rest, contactPhone: rest.contactPhone.trim() === '' ? null : rest.contactPhone });
      onSaved(saved);
    } catch (caught) { setError(labError(caught)); }
    finally { inFlight.current = false; setBusy(false); }
  }
  return <FormModal id="xo-org-form" title={organization === null ? text('add') : text('edit_organization')}
    subtitle={organization === null ? text('add_hint') : organization.name} busy={busy} onClose={onCancel} onSubmit={submit}
    submitLabel={org(busy ? 'saving' : 'save')} note={organization === null ? text('add_note_projects') : ''}>
    <FormSection title={text('the_organization')}>
      <div className="xo-product-form-grid">
        <Field id="xo-org-code" label={org('code')} hint={org(organization === null ? 'code_hint' : 'code_fixed')}>
          <input {...fieldAria('xo-org-code', { hint: true })} value={fields.code} onChange={set('code')} maxLength={32} disabled={busy || organization !== null} required />
        </Field>
        <Field id="xo-org-name" label={org('name')}>
          <input {...fieldAria('xo-org-name')} value={fields.name} onChange={set('name')} maxLength={255} disabled={busy} required />
        </Field>
        <Field id="xo-org-type" label={org('type')}>
          <XomicsSelect id="xo-org-type" value={fields.typeCode} onChange={set('typeCode')} disabled={busy}>
            <option value="">{org('choose_type')}</option>
            {types.filter((type) => type.isActive).map((type) => <option key={type.code} value={type.code}>{type.label}</option>)}
          </XomicsSelect>
        </Field>
        <Field id="xo-org-country" label={org('country')} hint={org('country_hint')}>
          <input {...fieldAria('xo-org-country', { hint: true })} value={fields.countryCode} onChange={set('countryCode')} maxLength={2} disabled={busy} required />
        </Field>
      </div>
    </FormSection>
    <FormSection title={text('their_contact')}>
      <div className="xo-product-form-grid">
        <Field id="xo-org-contact-name" label={org('contact_name')}>
          <input {...fieldAria('xo-org-contact-name')} value={fields.contactName} onChange={set('contactName')} maxLength={255} disabled={busy} required />
        </Field>
        <Field id="xo-org-contact-email" label={org('contact_email')}>
          <input {...fieldAria('xo-org-contact-email')} type="email" value={fields.contactEmail} onChange={set('contactEmail')} maxLength={320} disabled={busy} required />
        </Field>
        <Field id="xo-org-contact-phone" label={org('contact_phone')}>
          <input {...fieldAria('xo-org-contact-phone')} value={fields.contactPhone} onChange={set('contactPhone')} maxLength={32} disabled={busy} />
        </Field>
      </div>
      <Choice title={org('active')} detail={text('active_hint_projects')} on={fields.isActive} disabled={busy}
        onChange={(on) => setFields((current) => ({ ...current, isActive: on }))} />
    </FormSection>
    {error !== '' && <p className="xo-alert t-bad" role="alert">{error}</p>}
  </FormModal>;
}

// ------------------------------------------------------------------------------- a project

// Requested, Samples sent, In the lab, Done, as the portal draws a sample's journey: a check for
// each step passed, a dot for where it is now. A submission of a continuous project starts at sent.
function Steps({ stages, stage, detail }) {
  const reached = stages.indexOf(stage);
  const done = stage === 'done';
  return <div className="xo-sample-mini-journey xo-cust-journey" aria-label={text('stage_label')}>
    {stages.map((step, index) => {
      const state = done || index < reached ? 'is-previous' : index === reached ? 'is-current' : 'is-next';
      return <span key={step} className={state}>
        <i>{state === 'is-previous' && <Check aria-hidden="true" />}</i>
        <span><strong>{text(`stage.${step}`)}</strong><small>{index <= reached || done ? detail[step] : ''}</small></span>
      </span>;
    })}
  </div>;
}

// Where a project is. Withdrawn, in a word. A one-time project as the journey of its samples; a
// continuous one as open (or closed) with how many submissions it has had.
function Journey({ submission }) {
  if (submission.stage === 'withdrawn') {
    return <p className="xo-cust-withdrawn">{submission.withdrawnBy ? text('withdrawn_by', { name: submission.withdrawnBy, date: day(submission.withdrawnAt) })
      : text('withdrawn_on', { date: day(submission.withdrawnAt) })}</p>;
  }
  if (submission.kind === 'continuous') return <ContinuousState submission={submission} />;
  return <Steps stages={STAGES} stage={submission.stage} detail={{
    requested: day(submission.requestedAt),
    sent: submission.sentAt ? day(submission.sentAt) : '',
    in_lab: submission.sentAt ? text('received_of', { received: submission.receivedCount, count: submission.sentCount }) : '',
    done: '',
  }} />;
}

// A continuous project at a glance: open or closed, its submissions and samples so far, and how far
// those samples are.
function ContinuousState({ submission }) {
  return <div className="xo-cust-continuous">
    <span className="xo-cust-continuous-line">
      <Pill tone={STAGE_TONES[submission.stage]}>{text(`stage.${submission.stage}`)}</Pill>
      <span>{submission.submissions.length === 0 ? text('no_submissions_yet_line')
        : `${counted('boxes_count', submission.submissions.length)} · ${counted('samples_count', submission.sentCount)}`}</span>
    </span>
    {submission.sentCount > 0 && <ProgressBar progress={submission.progress} total={submission.sentCount} />}
  </div>;
}

// A submission of a project: Submitted, In the lab, Done.
function BoxJourney({ box }) {
  return <Steps stages={BOX_STAGES} stage={box.stage} detail={{
    sent: day(box.sentAt), in_lab: text('received_of', { received: box.receivedCount, count: box.sentCount }), done: '',
  }} />;
}

// What its invoices say at a glance: nothing billed yet (or only a pro forma sent), paid, still to
// be paid, or overdue.
function billing(submission) {
  const billed = submission.invoices.filter((invoice) => invoice.kind === 'invoice');
  const unpaid = billed.filter(toPay);
  const overdue = unpaid.filter((invoice) => invoice.status === 'overdue').length;
  if (billed.length === 0) return { tone: 'flat', line: text(submission.invoices.length > 0 ? 'proforma_only' : 'not_invoiced') };
  if (overdue > 0) return { tone: 'bad', line: text('overdue_count', { count: overdue }) };
  if (unpaid.length > 0) {
    return { tone: 'warn', line: text('still_to_pay', { amount: money(submission.invoicedMinor - submission.paidMinor, submission.currency, submission.minorUnit) }) };
  }
  return { tone: 'ok', line: text('all_paid') };
}

// The samples sent, as one bar: done, in the laboratory, received; the rest not yet arrived.
function ProgressBar({ progress, total }) {
  return <span className="xo-cust-bar" aria-hidden="true">
    {['completed', 'in_progress', 'received'].map((status) => progress[status] > 0
      && <i key={status} className={`is-${status}`} style={{ width: `${(progress[status] / Math.max(total, 1)) * 100}%` }} />)}
  </span>;
}

function stageLine(submission) {
  if (submission.stage === 'withdrawn') return text('withdrawn_short', { date: shortDay(submission.withdrawnAt) });
  if (submission.kind === 'continuous') {
    if (submission.stage === 'closed') return text('closed_short', { date: shortDay(submission.closedAt) });
    return submission.lastSentAt === null ? text('open_since_short', { date: shortDay(submission.requestedAt) })
      : text('last_sent_short', { date: shortDay(submission.lastSentAt) });
  }
  if (submission.sentAt === null) return text('asked_short', { date: shortDay(submission.requestedAt) });
  return text('sent_short', { date: shortDay(submission.sentAt), name: submission.sentBy || '' });
}

// What the sheet says under where a project is: who sent its samples and when, or that it waits for
// them; for a continuous one, that it is open (with how many submissions so far), or who closed it.
function whereLine(submission) {
  if (submission.kind === 'continuous') {
    if (submission.stage === 'closed') return text('closed_by_line', { name: submission.closedBy || '', date: day(submission.closedAt) });
    if (submission.stage !== 'open') return '';
    return submission.lastSentAt === null ? text('open_waiting_line')
      : text('open_line', { count: submission.submissions.length, date: day(submission.lastSentAt) });
  }
  if (submission.sentAt) return text('sent_by_line', { name: submission.sentBy || '', date: day(submission.sentAt), type: submission.sampleType?.name || '' });
  return submission.stage === 'requested' ? text('waiting_for_samples', { count: submission.sampleCount }) : '';
}

// What a project's price is in a few words: a one-time project's total; a continuous one's price
// for each sample.
const priceLine = (submission) => (submission.kind === 'continuous'
  ? text('price_each_short', { each: money(submission.unitPriceMinor, submission.currency, submission.minorUnit) })
  : money(submission.totalMinor, submission.currency, submission.minorUnit));

// One project in the list, as sample tracking lists a sample: what it is (one-time or continuous),
// where it is, and what it costs and what of that is paid.
function ProjectRow({ submission, onOpen }) {
  const bill = billing(submission);
  return <li>
    <button type="button" className={`xo-cust-sub is-${submission.stage}`} onClick={onOpen}
      aria-label={`${text('project', { number: submission.number })} · ${submission.workflow.name}`}>
      <span className="xo-cust-tile" aria-hidden="true">{submission.kind === 'continuous' ? <Repeat /> : <FlaskConical />}</span>
      <span className="xo-cust-cell">
        <strong className="xo-cust-ref">{text('project', { number: submission.number })}</strong>
        <span>{submission.workflow.name}</span>
        <small>{submission.kind === 'continuous' ? `${text('project_kind.continuous')} · ${stageLine(submission)}` : stageLine(submission)}</small>
      </span>
      <span className="xo-cust-sub-journey">
        {submission.stage === 'withdrawn' ? <span className="xo-cust-sub-withdrawn"><Pill tone="flat">{text('stage.withdrawn')}</Pill>{text('nothing_sent')}</span>
          : <Journey submission={submission} />}
      </span>
      <span className="xo-cust-cell is-money">
        <strong>{priceLine(submission)}</strong>
        <small className={`t-${bill.tone}`}>{bill.line}</small>
      </span>
      <ChevronRight className="xo-cust-open" aria-hidden="true" />
    </button>
  </li>;
}

// A card on the page with a heading, a line under it and what can be done there; then its rows.
function Surface({ title, detail, action, children }) {
  return <section className="xo-cust-surface">
    <header className="xo-cust-surface-head"><div><h2>{title}</h2>{detail && <p>{detail}</p>}</div>{action}</header>
    {children}
  </section>;
}

function ProjectsTab({ data, onOpen, onNew }) {
  const live = data.submissions.filter((submission) => submission.stage !== 'withdrawn');
  const withdrawn = data.submissions.filter((submission) => submission.stage === 'withdrawn');
  if (data.submissions.length === 0) {
    return <div className="xo-cust-empty"><Empty title={text('no_projects_yet')} detail={text('no_projects_hint')} />
      {data.mayBill && <button type="button" className="xo-btn" onClick={onNew}><Plus aria-hidden="true" />{text('new_project')}</button>}</div>;
  }
  return <div className="xo-cust-stack">
    <Surface title={text('tab.projects')} detail={text('projects_detail')}>
      {live.length === 0 ? <p className="xo-cust-quiet">{text('no_open_projects')}</p>
        : <ol className="xo-cust-rows">{live.map((submission) => <ProjectRow key={submission.uuid} submission={submission} onOpen={() => onOpen(submission.uuid)} />)}</ol>}
    </Surface>
    {withdrawn.length > 0 && <Surface title={text('withdrawn_title', { count: withdrawn.length })}>
      <ol className="xo-cust-rows">{withdrawn.map((submission) => <ProjectRow key={submission.uuid} submission={submission} onOpen={() => onOpen(submission.uuid)} />)}</ol>
    </Surface>}
  </div>;
}

// An invoice paid: the payment's reference, if there is one, and it is recorded.
function PayForm({ invoice, onDone, onCancel }) {
  const [reference, setReference] = useState('');
  const { busy, error, run } = useBusy();
  async function submit(event) {
    event.preventDefault();
    if (await run(() => markCustomerInvoicePaid(invoice.uuid, reference.trim() || null))) onDone();
  }
  return <form className="xo-cust-pay" onSubmit={submit}>
    <Field id={`pay-${invoice.uuid}`} label={text('payment_reference')}>
      <input {...fieldAria(`pay-${invoice.uuid}`)} value={reference} onChange={(event) => setReference(event.target.value)} maxLength={128} disabled={busy} autoFocus />
    </Field>
    <button type="button" className="xo-btn-2" onClick={onCancel} disabled={busy}>{text('cancel')}</button>
    <button type="submit" className="xo-btn" disabled={busy}>{text(busy ? 'saving' : 'confirm_paid')}</button>
    {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
  </form>;
}

// When it was paid (and who of ours marked it so), or when it is due; a pro forma has no due date.
function invoiceWhen(invoice) {
  if (invoice.status === 'proforma') return text('no_due_date');
  if (invoice.status !== 'paid') return text('due_date', { date: day(invoice.dueAt) });
  return invoice.paidBy ? text('paid_on_by', { date: day(invoice.paidAt), name: invoice.paidBy }) : text('paid_date', { date: day(invoice.paidAt) });
}

// What an invoice bills, in a few words: its samples and its other costs, and the company that issued it.
function linesLine(invoice) {
  const costs = invoice.lines.filter((line) => !line.billsSamples).length;
  const parts = [];
  if (invoice.samples > 0) parts.push(counted('samples_count', invoice.samples));
  if (costs > 0) parts.push(counted('costs_count', costs));
  if (invoice.issuer?.legalName) parts.push(invoice.issuer.legalName);
  return parts.join(' · ');
}

// Invoices as rows: number and what it bills (with its submission when the list holds several),
// amount, when, status, and what can be done with each (only an invoice is marked paid).
function InvoiceRows({ invoices, mayBill, withSubmission, onOpenSubmission, onChanged, fresh }) {
  const [paying, setPaying] = useState(null);
  return <ol className="xo-cust-rows">{invoices.map((invoice) => <li key={invoice.uuid} className={fresh === invoice.uuid ? 'is-new' : ''}>
    <div className={`xo-cust-invoice-row is-${invoice.status}${withSubmission ? ' has-submission' : ''}`}>
      <span className="xo-cust-tile" aria-hidden="true">{invoice.kind === 'proforma' ? <FileSearch /> : <Receipt />}</span>
      <span className="xo-cust-cell"><strong className="xo-cust-ref">{invoice.number}</strong><small>{linesLine(invoice)}</small></span>
      {withSubmission && <span className="xo-cust-cell">
        <button type="button" className="xo-cust-for" onClick={() => onOpenSubmission(invoice.submission.uuid)}>{text('project', { number: invoice.submission.number })}</button>
        <small>{invoice.submission.workflowName}</small></span>}
      <span className="xo-cust-cell is-money"><strong>{money(invoice.amountMinor, invoice.currency, invoice.minorUnit)}</strong><small>{text('issued_on', { date: shortDay(invoice.issuedAt) })}</small></span>
      <span className="xo-cust-cell"><Pill tone={INVOICE_TONES[invoice.status]}>{text(`invoice_status.${invoice.status}`)}</Pill><small>{invoiceWhen(invoice)}</small></span>
      <span className="xo-cust-actions">
        <button type="button" className="xo-btn-2" onClick={() => openDocument('invoice', invoice.uuid)} aria-label={text('open_invoice_named', { number: invoice.number })}>
          <ExternalLink aria-hidden="true" />{text('open')}</button>
        {mayBill && toPay(invoice) && paying !== invoice.uuid
          && <button type="button" className="xo-btn-2" onClick={() => setPaying(invoice.uuid)}>{text('mark_paid')}</button>}
      </span>
    </div>
    {paying === invoice.uuid && <PayForm invoice={invoice} onCancel={() => setPaying(null)} onDone={() => { setPaying(null); onChanged(text('marked_paid', { number: invoice.number })); }} />}
  </li>)}</ol>;
}

// Who an invoice is billed by: one of our billing profiles in use (from the customer's page, with the
// currencies each has a bank account in). The only one there is is chosen for you.
const billsIn = (profile, currency) => Boolean(profile && profile.currencies.includes(currency));
const onlyProfile = (profiles) => (profiles.length === 1 ? String(profiles[0].id) : '');

function BilledBy({ id, profiles, value, onChange, disabled, kind = 'invoice' }) {
  const hint = profiles.length === 0 ? 'no_profiles_for_invoice' : kind === 'proforma' ? 'billed_by_proforma_hint' : 'billed_by_hint';
  return <Field id={id} label={text('billed_by')} hint={text(hint)}>
    <XomicsSelect id={id} value={value} onChange={(event) => onChange(event.target.value)} disabled={disabled || profiles.length === 0}>
      {profiles.length !== 1 && <option value="">{text('choose_profile')}</option>}
      {profiles.map((profile) => <option key={profile.id} value={profile.id}>{profile.legalName} · {profile.countryCode}</option>)}
    </XomicsSelect>
  </Field>;
}

// What stops an invoice billed by the profile chosen in a currency, said under the form: no profile yet, or none chosen,
// or no bank account of it in that currency.
function billingProblem(profiles, chosen, currency) {
  if (profiles.length === 0) return text('no_profiles_for_invoice');
  if (!chosen) return null;
  return billsIn(chosen, currency) ? null : text('profile_no_account', { profile: chosen.legalName, currency });
}

// Another invoice for a project, at its price: an invoice to pay by default (the pro forma
// usually came with the project), or another pro forma; its samples not invoiced yet by default
// (of those sent, once they are: for a continuous project, those sent since its last invoice), and
// other costs, one line each, billed by the profile chosen.
function InvoiceForm({ submission, customerName, billingProfiles, onDone, onCancel }) {
  const all = submission.sentCount > 0 || submission.kind === 'continuous' ? submission.sentCount : submission.sampleCount;
  const [samples, setSamples] = useState(String(Math.max(all - submission.invoicedSamples, 0)));
  const [costs, setCosts] = useState([]);
  const [kind, setKind] = useState('invoice');
  const [dueDays, setDueDays] = useState('14');
  const [profileId, setProfileId] = useState(() => onlyProfile(billingProfiles));
  const chosen = billingProfiles.find((profile) => String(profile.id) === profileId) ?? null;
  const problem = billingProblem(billingProfiles, chosen, submission.currency);
  const { busy, error, run, setError } = useBusy();
  const count = Number(samples);
  const countOk = samples !== '' && Number.isInteger(count) && count >= 0;
  const read = costsOf(costs, submission.minorUnit);
  const samplesMinor = countOk ? count * submission.unitPriceMinor : null;
  const totalMinor = samplesMinor === null || read === null ? null : samplesMinor + read.reduce((sum, cost) => sum + cost.amountMinor, 0);
  async function submit(event) {
    event.preventDefault();
    if (!countOk || read === null) { setError(text('fill_lines')); return; }
    if (!billsIn(chosen, submission.currency)) { setError(problem || text('choose_profile')); return; }
    const made = await run(() => createCustomerInvoice(submission.uuid, { kind, sampleCount: count, costs: read,
      ...(kind === 'invoice' ? { dueDays: Number(dueDays) } : {}), billingProfileId: Number(profileId) }));
    if (made) onDone(made);
  }
  return <form className="xo-pm-card xo-cust-invoice-form" onSubmit={submit} noValidate>
    <div className="xo-pm-section-head"><div><h3>{text('new_invoice')}</h3><p>{text('new_invoice_detail', { name: customerName })}</p></div></div>
    <InvoiceKinds value={kind} onChange={setKind} disabled={busy} />
    <DueTerms id="invoice-due" kind={kind} dueDays={dueDays} setDueDays={setDueDays} disabled={busy} />
    <div className="xo-product-form-grid">
      <div className="xo-product-field-wide">
        <BilledBy id="invoice-billed-by" profiles={billingProfiles} value={profileId} onChange={setProfileId} disabled={busy} kind={kind} />
      </div>
      <Field id="invoice-samples" label={text('invoice_samples')}
        hint={submission.kind === 'continuous' ? text('invoice_samples_continuous', { sent: submission.sentCount, invoiced: submission.invoicedSamples })
          : submission.invoicedSamples > 0 ? text('already_invoiced', { count: submission.invoicedSamples, total: all }) : text('invoice_samples_hint_project', { count: all })}>
        <input {...fieldAria('invoice-samples', { hint: true })} type="number" min={0} max={100000} value={samples} onChange={(event) => setSamples(event.target.value)} disabled={busy} />
      </Field>
    </div>
    <CostLines idPrefix="invoice" costs={costs} setCosts={setCosts} currency={submission.currency} disabled={busy} />
    <Totals currency={submission.currency} minorUnit={submission.minorUnit} totalMinor={totalMinor} totalLabel={text('invoice_total_label')}
      lines={[{ key: 'samples', label: text('quote_line', { count: countOk ? count : 0, each: money(submission.unitPriceMinor, submission.currency, submission.minorUnit) }), minor: samplesMinor },
        ...costs.map((cost) => ({ key: cost.key, label: cost.description.trim() || text('cost_untitled'), minor: toMinor(cost.amount, submission.minorUnit) }))]} />
    {problem && <p className="xo-alert t-warn">{problem}</p>}
    {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
    <div className="xo-cust-form-actions">
      <button type="button" className="xo-btn-2" onClick={onCancel} disabled={busy}>{text('cancel')}</button>
      <button type="submit" className="xo-btn" disabled={busy || totalMinor === null || totalMinor <= 0 || !billsIn(chosen, submission.currency)}>
        {kind === 'proforma' ? <FileSearch aria-hidden="true" /> : <Receipt aria-hidden="true" />}
        {text(busy ? 'saving' : kind === 'proforma' ? 'make_proforma' : 'make_pro_forma')}</button>
    </div>
  </form>;
}

// Whether the customer asked for patient reports on a submission, and where those reports are: asked
// (by whom, when, in which language, and how many are ready), said no, or not answered yet once the
// laboratory is done. We see the reports, never the people they are for. A made report reaches the
// customer once the sample's results are approved on the Validation page.
function ReportsLine({ box }) {
  const answer = box.reports?.answer;
  if (answer === 'wanted') {
    const reports = box.samples.flatMap((sample) => sample.patientReport?.reports ?? []);
    // How many are ready, waiting for validation, being made, held back, or not made; only what there is.
    const counts = ['ready', 'review', 'making', 'held', 'failed'].map((status) => [status, reports.filter((report) => report.status === status).length])
      .filter(([, count]) => count > 0).map(([status, count]) => text(`reports_${status}_count`, { count }));
    return <p className="xo-cust-reports-line"><FileSearch aria-hidden="true" />
      <span>{text('reports_wanted_line', { name: box.reports.answeredBy || '', date: day(box.reports.answeredAt), language: box.reports.language?.name || '' })}
        <small>{counts.length > 0 ? counts.join(' · ') : text('reports_none_yet')}</small></span></p>;
  }
  if (answer === 'declined') {
    return <p className="xo-cust-reports-line is-quiet">{text('reports_declined_line', { name: box.reports.answeredBy || '', date: day(box.reports.answeredAt) })}</p>;
  }
  if (box.reportsOffered) return <p className="xo-cust-reports-line is-quiet">{text('reports_unanswered_line')}</p>;
  return null;
}

// A sample's patient reports, for us: each report with its type and state, or waiting for its
// analysis while there is none yet. No report was asked for a sample without a person.
function SampleReports({ sample }) {
  if (!sample.patientReport) return <span className="xo-muted">—</span>;
  if (sample.patientReport.reports.length === 0) return <small>{text('report_waiting')}</small>;
  return <span className="xo-cust-report-pills">{sample.patientReport.reports.map((report) => <Pill key={report.uuid} tone={REPORT_TONES[report.status]}>
    {report.type.name}: {text(`report_status.${report.status}`)}</Pill>)}</span>;
}

// One submission of a project, in its sheet: when and by whom it was sent, of which sample type and
// with their note, where its samples are (their names beside our codes, to label the tubes when the
// box arrives), what they wrote of each in the laboratory's submission form, and its patient reports.
// The files of a submission: the customer's and our laboratory's, each opened in a new tab.
function BoxFiles({ box }) {
  const [error, setError] = useState('');
  if (box.files.length === 0) return null;
  return <div className="xo-cust-files">
    <strong>{text('files')}</strong>
    <ul>{box.files.map((file) => <li key={file.uuid}>
      <button type="button" className="xo-link" onClick={() => openCustomerFile(file.uuid).catch((caught) => setError(labError(caught)))}>{file.name}</button>
      <small>{text(file.side === 'customer' ? 'file_theirs' : 'file_ours')} · {fileSize(file.bytes)} · {day(file.addedAt)}</small>
    </li>)}</ul>
    {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
  </div>;
}

function BoxCard({ box, columns }) {
  const reported = box.reports?.answer === 'wanted';
  const checked = box.samples.some((sample) => sample.qc !== null);
  const shown = filledColumns(columns, box.samples);
  return <div className="xo-pm-card xo-cust-box">
    <div className="xo-pm-section-head"><div>
      <h3>{text('box', { number: box.number })}</h3>
      <p>{[text('box_line', { name: box.sentBy || '', date: day(box.sentAt) }), box.sampleType.name, counted('samples_count', box.sentCount)].join(' · ')}</p>
    </div><Pill tone={STAGE_TONES[box.stage]}>{text(`stage.${box.stage}`)}</Pill></div>
    <BoxJourney box={box} />
    {box.note && <p className="xo-pm-hint">{text('their_note')} {box.note}</p>}
    <ReportsLine box={box} />
    {box.qcWaiting > 0 && <p className="xo-pm-hint">{counted('qc_waiting', box.qcWaiting)}</p>}
    {box.qcSent && <p className="xo-pm-hint">{text('qc_sent', { date: day(box.qcSent.at), name: box.qcSent.by || '' })}</p>}
    <div className="xo-cust-table-wrap"><table className="xo-cust-table">
      <thead><tr><th>{text('their_name')}</th><th>{text('our_code')}</th><th>{text('where')}</th>
        {checked && <th>{text('qc')}</th>}
        {reported && <th>{text('patient_report')}</th>}
        {shown.map((column) => <th key={column.code}>{column.label}</th>)}</tr></thead>
      <tbody>{box.samples.map((sample) => <tr key={sample.code}><td>{sample.sampleName}</td><td><code>{sample.code}</code></td>
        <td><Pill tone={SAMPLE_TONES[sample.status] || 'flat'}>{text(`sample_status.${sample.status}`)}</Pill>
          {sample.status === 'in_progress' && sample.step && <small> {text('step_of', { done: sample.completedSteps + 1, total: sample.totalSteps, step: sample.step })}</small>}</td>
        {checked && <td>{sample.qc && <><Pill tone={QC_TONES[qcResult(sample.qc)]}>{tx(`ui.customer_portal.qc_result.${qcResult(sample.qc)}`)}</Pill>
          {sample.qc.answer && <small> {text(sample.qc.answer.byCustomer ? 'qc_answer_by' : 'qc_answer_for', { name: sample.qc.answer.by || '', date: day(sample.qc.answer.decidedAt) })}</small>}</>}</td>}
        {reported && <td><SampleReports sample={sample} /></td>}
        {shown.map((column) => <td key={column.code}>{answerText(column, sample.answers[column.code])}</td>)}</tr>)}</tbody>
    </table></div>
    <BoxFiles box={box} />
  </div>;
}

// One project, as a sheet over the customer: where it is and what it costs, its submissions (each
// with its samples), and its invoices (more can be made there). One with nothing sent yet can be
// withdrawn; a continuous one can be closed to new submissions.
function SubmissionSheet({ uuid, customerName, mayBill, billingProfiles, onClose, onChanged }) {
  const load = useCallback(() => loadCustomerSubmission(uuid), [uuid]);
  const resource = useLiveResource(['organizations', 'samples', 'reports'], load);
  const [tab, setTab] = useState('overview'), [invoicing, setInvoicing] = useState(false), [confirming, setConfirming] = useState(null);
  const [toast, setToast] = useState(''), [fresh, setFresh] = useState(null);
  const decide = useBusy();
  useEscape(onClose);
  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(''), 5000);
    return () => window.clearTimeout(timer);
  }, [toast]);
  const changed = (message) => { resource.refresh(); onChanged(); if (message) setToast(message); };
  const data = resource.data;
  const submission = data?.submission;
  const continuous = submission?.kind === 'continuous';
  const tabs = submission ? [
    { value: 'overview', label: text('sheet_overview') },
    { value: 'submissions', label: text('sheet_submissions'), count: submission.submissions.length },
    { value: 'invoices', label: text('sheet_invoices'), count: submission.invoices.length },
  ] : [];
  // Withdrawing (nothing sent for it yet) and closing (a continuous one, open) are each asked once more.
  const mayWithdraw = mayBill && submission && submission.stage !== 'withdrawn' && submission.submissions.length === 0;
  const mayClose = mayBill && continuous && submission.stage === 'open';
  const confirm = confirming === 'withdraw'
    ? { title: text('withdraw_project_title', { number: submission?.number }), detail: text('withdraw_detail'), keep: text('keep_it'), act: text('withdraw'),
      work: () => withdrawCustomerSubmission(submission.uuid), done: text('withdrawn_project_toast', { number: submission?.number }) }
    : { title: text('close_project_title', { number: submission?.number }), detail: text('close_project_detail'), keep: text('keep_it_open'),
      act: text('close_project'), work: () => closeCustomerProject(submission.uuid), done: text('closed_project_toast', { number: submission?.number }) };
  return <div className="xo-product-modal-backdrop" role="presentation" onMouseDown={onClose}>
    <section className="xo-pm xo-cust-sheet" role="dialog" aria-modal="true" aria-labelledby="xo-cust-sheet-title" onMouseDown={(event) => event.stopPropagation()}>
      <header className="xo-pm-head">
        <span className="xo-pm-thumb">{continuous ? <Repeat aria-hidden="true" /> : <FlaskConical aria-hidden="true" />}</span>
        <div className="xo-pm-title">
          <h2 id="xo-cust-sheet-title">{submission ? text('project', { number: submission.number }) : text('loading')}</h2>
          {submission && <p><span>{submission.workflow.name}</span><span>{text(`project_kind.${submission.kind}`)}</span>
            <Pill tone={STAGE_TONES[submission.stage]}>{text(`stage.${submission.stage}`)}</Pill>
            <span>{submission.requestedBy ? text('asked_line', { name: submission.requestedBy, date: day(submission.requestedAt) }) : day(submission.requestedAt)}</span></p>}
        </div>
        <div className="xo-pm-head-actions">
          {mayClose && confirming === null && <button type="button" className="xo-btn-2" onClick={() => setConfirming('close')}>{text('close_project')}</button>}
          {mayWithdraw && confirming === null && <button type="button" className="xo-btn-2" onClick={() => setConfirming('withdraw')}>{text('withdraw')}</button>}
          <button type="button" className="xo-icon-btn" aria-label={text('close')} onClick={onClose}><X aria-hidden="true" /></button>
        </div>
      </header>
      {submission && <div className="xo-pm-tabbar">
        <WorkspaceTabs className="xo-pm-tabs" value={tab} onChange={setTab} tabs={tabs} label={text('sheet_project_sections')} />
      </div>}
      <div className="xo-pm-body">
        {resource.error ? <Failed message={resource.error} onRetry={resource.refresh} /> : data === null ? <Loading label={text('loading')} /> : <>
          {confirming !== null && <div className="xo-cust-confirm" role="alertdialog" aria-labelledby="xo-cust-confirm">
            <p id="xo-cust-confirm"><strong>{confirm.title}</strong>{confirm.detail}</p>
            <div>
              <button type="button" className="xo-btn-2" onClick={() => { setConfirming(null); decide.setError(''); }} disabled={decide.busy}>{confirm.keep}</button>
              <button type="button" className="xo-btn" disabled={decide.busy} onClick={async () => {
                if (await decide.run(confirm.work)) { setConfirming(null); changed(confirm.done); }
              }}>{confirm.act}</button>
            </div>
            {decide.error && <p className="xo-alert t-bad" role="alert">{decide.error}</p>}
          </div>}

          <div className="xo-pm-panel xo-cust-panel" hidden={tab !== 'overview'}>
            <div className="xo-pm-card">
              <h3>{text('where_it_is')}</h3>
              <Journey submission={submission} />
              <p className="xo-pm-hint">{whereLine(submission)}
                {submission.shipTo && <> {text('goes_to', { lab: submission.shipTo.name, city: submission.shipTo.city })}</>}</p>
            </div>
            <div className="xo-pm-facts">
              <div className="xo-pm-fact"><span>{text('price')}</span><strong>{continuous ? priceLine(submission) : money(submission.totalMinor, submission.currency, submission.minorUnit)}</strong>
                <small>{continuous ? text('so_far', { amount: money(submission.totalMinor, submission.currency, submission.minorUnit) })
                  : text('quote_line', { count: submission.sampleCount, each: money(submission.unitPriceMinor, submission.currency, submission.minorUnit) })}</small></div>
              <div className="xo-pm-fact"><span>{text('samples')}</span>
                <strong>{submission.submissions.length === 0 ? (continuous ? text('none_sent_yet') : counted('samples_asked', submission.sampleCount))
                  : text('received_of', { received: submission.receivedCount, count: submission.sentCount })}</strong>
                <small>{submission.submissions.length === 0 ? text('not_sent_yet') : text('progress_short', { lab: submission.progress.in_progress, done: submission.progress.completed })}</small>
                {submission.submissions.length > 0 && <ProgressBar progress={submission.progress} total={submission.sentCount} />}</div>
              <div className="xo-pm-fact"><span>{text('invoiced')}</span><strong>{money(submission.invoicedMinor, submission.currency, submission.minorUnit)}</strong>
                <small>{continuous ? text('not_invoiced_yet', { count: Math.max(submission.sentCount - submission.invoicedSamples, 0) })
                  : <>{text('invoices_count', { count: submission.invoices.filter((invoice) => invoice.kind === 'invoice').length })}
                    {submission.invoices.some((invoice) => invoice.kind === 'proforma')
                      && ` · ${counted('proforma_count', submission.invoices.filter((invoice) => invoice.kind === 'proforma').length)}`}</>}</small></div>
              <div className="xo-pm-fact"><span>{text('paid')}</span><strong>{money(submission.paidMinor, submission.currency, submission.minorUnit)}</strong>
                <small className={`t-${billing(submission).tone}`}>{billing(submission).line}</small></div>
            </div>
            <div className="xo-pm-card">
              <h3>{text('what_it_costs')}</h3>
              <table className="xo-cust-price">
                <tbody>
                  <tr><td>{submission.workflow.name}</td><td>{continuous
                    ? text('sent_at_each', { count: submission.sentCount, each: money(submission.unitPriceMinor, submission.currency, submission.minorUnit) })
                    : text('quote_line', { count: submission.sampleCount, each: money(submission.unitPriceMinor, submission.currency, submission.minorUnit) })}</td>
                    <td>{money(submission.processingMinor, submission.currency, submission.minorUnit)}</td></tr>
                  {submission.costs.map((cost, index) => <tr key={index}><td>{cost.description}</td><td /><td>{money(cost.amountMinor, submission.currency, submission.minorUnit)}</td></tr>)}
                </tbody>
                <tfoot><tr><td>{text(continuous ? 'total_so_far' : 'total')}</td><td /><td>{money(submission.totalMinor, submission.currency, submission.minorUnit)}</td></tr></tfoot>
              </table>
            </div>
            {submission.requestNote && <div className="xo-pm-card">
              <h3>{text('notes')}</h3>
              <dl className="xo-cust-facts">
                <div><dt>{text('our_note')}</dt><dd>{submission.requestNote}</dd></div>
              </dl>
            </div>}
          </div>

          <div className="xo-pm-panel xo-cust-panel" hidden={tab !== 'submissions'}>
            {submission.submissions.length === 0 ? <div className="xo-pm-card"><Empty title={text('no_samples_yet')}
              detail={text(continuous ? 'no_boxes_hint' : 'no_samples_hint')} /></div> : <>
              <p className="xo-pm-hint">{text('label_hint')}</p>
              {[...submission.submissions].reverse().map((box) => <BoxCard key={box.number} box={box} columns={data.columns} />)}
            </>}
          </div>

          <div className="xo-pm-panel xo-cust-panel" hidden={tab !== 'invoices'}>
            <div className="xo-pm-section-head"><div><h3>{text('sheet_invoices')}</h3><p>{text(continuous ? 'sheet_invoices_continuous' : 'sheet_invoices_project')}</p></div>
              {mayBill && submission.stage !== 'withdrawn' && !invoicing
                && <button type="button" className="xo-btn" onClick={() => setInvoicing(true)}><Plus aria-hidden="true" />{text('new_invoice')}</button>}</div>
            {invoicing && <InvoiceForm submission={submission} customerName={customerName} billingProfiles={billingProfiles} onCancel={() => setInvoicing(false)}
              onDone={(made) => { setInvoicing(false); setFresh(made.invoice.uuid);
                changed(`${text(made.invoice.kind === 'proforma' ? 'proforma_made' : 'invoice_made', { number: made.invoice.number })} ${toldLine(made, customerName)}`); }} />}
            {submission.invoices.length === 0 ? !invoicing && <div className="xo-pm-card"><Empty title={text('no_invoices_on_submission')} detail={text('no_invoices_submission_hint')} /></div>
              : <div className="xo-cust-surface"><InvoiceRows invoices={submission.invoices} mayBill={mayBill} fresh={fresh} onChanged={changed} /></div>}
          </div>
        </>}
      </div>
      {toast && <p className="xo-pm-toast" role="status"><Check aria-hidden="true" />{toast}</p>}
    </section>
  </div>;
}

// The two kinds of project side by side, each with what it means, as the two kinds of invoice are.
const PROJECT_KINDS = Object.freeze([{ kind: 'one_time', Icon: Package }, { kind: 'continuous', Icon: Repeat }]);

function ProjectKinds({ value, onChange, disabled }) {
  return <div className="xo-cust-kinds" role="radiogroup" aria-label={text('project_kind_title')}>
    {PROJECT_KINDS.map(({ kind, Icon }) => <button type="button" key={kind} role="radio" aria-checked={value === kind} disabled={disabled}
      className={`xo-cust-kind is-${kind}${value === kind ? ' is-on' : ''}`} onClick={() => onChange(kind)}>
      <span className="xo-cust-kind-head">
        <span className="xo-cust-kind-art" aria-hidden="true"><Icon /></span>
        <span className="xo-cust-kind-tick" aria-hidden="true">{value === kind && <Check />}</span>
      </span>
      <strong>{text(`project_kind.${kind}`)}</strong>
      <small>{text(`project_kind.${kind}_line`)}</small>
      <ul>{[1, 2, 3].map((n) => <li key={n}><CircleCheck aria-hidden="true" />{text(`project_kind.${kind}_fact_${n}`)}</li>)}</ul>
    </button>)}
  </div>;
}

// A new project: one-time or continuous; the workflow; for a one-time project how many samples,
// their price (for each, or for all of them, either one setting the other) and its first invoice
// (for all of it) when it is wanted at once: a pro forma for them to look over (as usual; the
// invoice to pay follows later), or the invoice itself with its due date; for a continuous one the
// price for each sample only, as it is invoiced as the samples come. Other costs and a note to them
// either way. It waits in their portal for their samples; everyone with an account is emailed once.
function NewSubmissionModal({ data, options, onClose, onDone }) {
  const last = data.submissions[0];
  const [projectKind, setProjectKind] = useState('one_time');
  const continuous = projectKind === 'continuous';
  const [workflowId, setWorkflowId] = useState('');
  // Our laboratory the samples go to: the last submission's, or our first.
  const [labId, setLabId] = useState(String(last?.shipTo?.id ?? options.laboratories?.[0]?.id ?? ''));
  const [currencyCode, setCurrencyCode] = useState(last ? last.currency : options.currencies[0]?.code || '');
  const [samples, setSamples] = useState(''), [each, setEach] = useState(''), [processing, setProcessing] = useState(''), [driver, setDriver] = useState('each');
  const [costs, setCosts] = useState([]), [note, setNote] = useState(''), [invoice, setInvoice] = useState(true), [dueDays, setDueDays] = useState('14');
  const [kind, setKind] = useState('proforma');
  const profiles = data.billingProfiles;
  const [profileId, setProfileId] = useState(() => onlyProfile(profiles));
  const chosen = profiles.find((profile) => String(profile.id) === profileId) ?? null;
  const { busy, error, run, setError } = useBusy();
  const currency = options.currencies.find((entry) => entry.code === currencyCode) || { code: currencyCode, minorUnit: 2 };
  const unit = currency.minorUnit;
  const count = Number(samples);
  const countOk = samples !== '' && Number.isInteger(count) && count >= 1;
  // The price for each sample, or the price for all of them: the one typed sets the other.
  const fromEach = (value, howMany) => { const minor = toMinor(value, unit); setProcessing(minor === null || !howMany ? '' : fromMinor(minor * howMany, unit)); };
  const fromAll = (value, howMany) => { const minor = toMinor(value, unit); setEach(minor === null || !howMany ? '' : fromMinor(Math.round(minor / howMany), unit)); };
  const changeEach = (value) => { setEach(value); setDriver('each'); fromEach(value, countOk ? count : 0); };
  const changeAll = (value) => { setProcessing(value); setDriver('all'); fromAll(value, countOk ? count : 0); };
  const changeSamples = (value) => {
    setSamples(value);
    const howMany = Number(value);
    if (!(Number.isInteger(howMany) && howMany >= 1)) return;
    if (driver === 'all') fromAll(processing, howMany); else fromEach(each, howMany);
  };
  const eachMinor = toMinor(each, unit);
  const exact = countOk && eachMinor !== null ? eachMinor * count : null;
  const typed = toMinor(processing, unit);
  const uneven = driver === 'all' && exact !== null && typed !== null && typed !== exact;
  const read = costsOf(costs, unit);
  const totalMinor = exact === null || read === null ? null : exact + read.reduce((sum, cost) => sum + cost.amountMinor, 0);
  // A first invoice can be made at once, with a one-time project, when one of our billing profiles
  // has a bank account in the price's currency.
  const invoiceable = profiles.some((profile) => billsIn(profile, currency.code));
  const invoicing = !continuous && invoice && invoiceable;
  const noInvoice = text(profiles.length === 0 ? 'no_profiles_for_invoice' : 'no_profile_in_currency', { currency: currency.code });
  const problem = invoicing ? billingProblem(profiles, chosen, currency.code) : null;
  const billable = !invoicing || billsIn(chosen, currency.code);
  const ready = workflowId !== '' && labId !== '' && (continuous || countOk) && eachMinor !== null && read !== null && billable;
  async function submit(event) {
    event.preventDefault();
    if (!billable) { setError(problem || text('choose_profile')); return; }
    if (!ready) { setError(text(continuous ? 'fill_lines_continuous' : 'fill_lines')); return; }
    const made = await run(() => createCustomerSubmission(data.customer.id, { kind: projectKind, workflowId: Number(workflowId),
      ...(continuous ? {} : { sampleCount: count }), unitPriceMinor: eachMinor,
      currency: currency.code, shipToLocationId: Number(labId), costs: read, note: note.trim() || null,
      invoice: invoicing ? { kind, ...(kind === 'invoice' ? { dueDays: Number(dueDays) } : {}), billingProfileId: Number(profileId) } : null }));
    if (made) onDone(made);
  }
  return <FormModal id="xo-new-submission" title={text('new_project')} subtitle={text('new_project_for', { name: data.customer.name })}
    busy={busy} onClose={onClose} onSubmit={submit} submitLabel={text(busy ? 'sending' : 'make_project')} submitDisabled={!ready}
    note={text(invoicing ? 'emails_everyone_project_invoice' : 'emails_everyone', { name: data.customer.name })}>
    <FormSection number="1" title={text('what_we_ask')} detail={text(continuous ? 'what_we_ask_continuous' : 'what_we_ask_detail')}>
      <ProjectKinds value={projectKind} onChange={setProjectKind} disabled={busy} />
      <div className="xo-product-form-grid">
        <div className="xo-product-field-wide"><Field id="ns-workflow" label={text('workflow')} hint={text('workflow_fixed_hint')}>
          <XomicsSelect id="ns-workflow" value={workflowId} onChange={(event) => setWorkflowId(event.target.value)} disabled={busy}>
            <option value="">{text('choose_workflow')}</option>
            {options.workflows.map((workflow) => <option key={workflow.id} value={workflow.id}>{workflow.name}</option>)}
          </XomicsSelect>
        </Field></div>
        {!continuous && <Field id="ns-samples" label={text('samples')}>
          <input {...fieldAria('ns-samples')} type="number" min={1} max={100000} value={samples} onChange={(event) => changeSamples(event.target.value)} disabled={busy} />
        </Field>}
        <Field id="ns-lab" label={text('ship_to')} hint={text('ship_to_hint')}>
          <XomicsSelect id="ns-lab" value={labId} onChange={(event) => setLabId(event.target.value)} disabled={busy}>
            {(options.laboratories || []).length === 0 && <option value="">{text('no_laboratory')}</option>}
            {(options.laboratories || []).map((place) => <option key={place.id} value={place.id}>{place.name} · {place.city}</option>)}
          </XomicsSelect>
        </Field>
      </div>
    </FormSection>
    <FormSection number="2" title={text('price')} detail={text(continuous ? 'price_detail_continuous' : 'price_detail')}>
      <div className="xo-product-form-grid">
        <div className="xo-product-field-wide"><Field id="ns-currency" label={text('bill_in')}
          hint={invoiceable ? text('bill_in_hint', { currency: currency.code }) : noInvoice}>
          <XomicsSelect id="ns-currency" value={currencyCode} onChange={(event) => setCurrencyCode(event.target.value)} disabled={busy}>
            {options.currencies.map((entry) => <option key={entry.code} value={entry.code}>{entry.code} · {entry.name}</option>)}
          </XomicsSelect>
        </Field></div>
        <Field id="ns-each" label={text('price_each')}>
          <MoneyInput id="ns-each" value={each} onChange={changeEach} currency={currency.code} disabled={busy} />
        </Field>
        {!continuous && <Field id="ns-all" label={text('processing_total')}
          hint={uneven ? text('uneven_total', { count, each: money(eachMinor, currency.code, unit), total: money(exact, currency.code, unit) }) : ''}>
          <MoneyInput id="ns-all" value={processing} onChange={changeAll} currency={currency.code} disabled={busy} describedBy={uneven ? 'ns-all-hint' : undefined}
            onBlur={() => { if (uneven) setProcessing(fromMinor(exact, unit)); }} />
        </Field>}
      </div>
      <div className="xo-cust-subhead"><h4>{text('other_costs')}</h4><p>{text('other_costs_detail')}</p></div>
      <CostLines idPrefix="ns" costs={costs} setCosts={setCosts} currency={currency.code} disabled={busy} />
      {continuous ? costs.length > 0 && <Totals currency={currency.code} minorUnit={unit} totalLabel={text('other_costs_total')}
        totalMinor={read === null ? null : read.reduce((sum, cost) => sum + cost.amountMinor, 0)}
        lines={costs.map((cost) => ({ key: cost.key, label: cost.description.trim() || text('cost_untitled'), minor: toMinor(cost.amount, unit) }))} />
        : <Totals currency={currency.code} minorUnit={unit} totalMinor={totalMinor} totalLabel={text('total')}
          lines={[{ key: 'samples', label: text('processing_total'), minor: exact },
            ...costs.map((cost) => ({ key: cost.key, label: cost.description.trim() || text('cost_untitled'), minor: toMinor(cost.amount, unit) }))]} />}
    </FormSection>
    <FormSection number="3" title={text('note_to_them')} detail={text('note_to_them_detail')}>
      <div className="xomics-field"><textarea aria-label={text('note_to_them')} rows={3} value={note} onChange={(event) => setNote(event.target.value)}
        maxLength={1000} disabled={busy} /></div>
    </FormSection>
    {continuous ? <FormSection number="4" title={text('invoices_title')}>
      <p className="xo-cust-kind-note">{text('continuous_invoices_note')}</p>
    </FormSection> : <FormSection number="4" title={text('first_invoice_title')}>
      <Choice title={text('first_invoice')} detail={invoiceable ? text('first_invoice_project_hint') : noInvoice}
        on={invoicing} onChange={setInvoice} disabled={busy || !invoiceable}>
        {invoicing && <div className="xo-cust-option-more">
          <InvoiceKinds value={kind} onChange={setKind} disabled={busy} />
          <DueTerms id="ns-due" kind={kind} dueDays={dueDays} setDueDays={setDueDays} disabled={busy} />
          <div className="xo-cust-billed-by"><BilledBy id="ns-billed-by" profiles={profiles} value={profileId} onChange={setProfileId} disabled={busy} kind={kind} /></div>
          {problem && <p className="xo-alert t-warn">{problem}</p>}
        </div>}
      </Choice>
    </FormSection>}
    {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
  </FormModal>;
}

// ------------------------------------------------------------------------------- invoices

const INVOICE_FILTERS = ['all', 'open', 'overdue', 'paid', 'proforma'];

// What invoices add up to, for each currency they are in: invoiced, paid, still to be paid, overdue.
// A pro forma bills nothing, so it adds nothing.
function totalsOf(invoices) {
  const byCurrency = new Map();
  for (const invoice of invoices.filter((entry) => entry.kind === 'invoice')) {
    const entry = byCurrency.get(invoice.currency) || { currency: invoice.currency, minorUnit: invoice.minorUnit, invoiced: 0, paid: 0, open: 0, overdue: 0 };
    entry.invoiced += invoice.amountMinor;
    if (invoice.status === 'paid') entry.paid += invoice.amountMinor;
    else entry.open += invoice.amountMinor;
    if (invoice.status === 'overdue') entry.overdue += invoice.amountMinor;
    byCurrency.set(invoice.currency, entry);
  }
  return [...byCurrency.values()];
}
// A figure over several currencies: in the view currency when one is chosen, each currency's sum otherwise.
function Total({ totals, field, as: Tag = 'dd', className = '' }) {
  const totalOf = useMoneyTotal();
  const shown = totalOf(totals.map((entry) => ({ minor: entry[field], currency: entry.currency, minorUnit: entry.minorUnit })));
  return <>
    <Tag className={className} title={shown.title ?? undefined}>{shown.text ?? money(0, totals[0]?.currency || 'EUR', 2)}</Tag>
    {shown.note && <small className="xo-in-view">{shown.note}</small>}
  </>;
}

// Every invoice of theirs, newest first, each with the submission it bills (which opens it).
function InvoicesTab({ data, onOpenSubmission, onChanged }) {
  const [filter, setFilter] = useState('all');
  const totals = totalsOf(data.invoices);
  const matches = (invoice, value) => value === 'all' || (value === 'open' ? toPay(invoice) : invoice.status === value);
  const shown = data.invoices.filter((invoice) => matches(invoice, filter));
  if (data.invoices.length === 0) return <div className="xo-cust-empty"><Empty title={text('no_invoices_yet')} detail={text('no_invoices_hint_projects')} /></div>;
  return <div className="xo-cust-stack">
    <dl className="xo-cust-sums">
      {['invoiced', 'paid', 'open', 'overdue'].map((key) => <div key={key}><dt>{text(`total_${key}`)}</dt>
        <Total totals={totals} field={key} className={key === 'overdue' && totals.some((entry) => entry.overdue > 0) ? 't-bad' : ''} /></div>)}
    </dl>
    <Surface title={text('tab.invoices')} detail={text('invoices_detail_projects')}
      action={<WorkspaceTabs className="xo-cust-filter" value={filter} onChange={setFilter} label={text('invoice_filter')}
        tabs={INVOICE_FILTERS.map((value) => ({ value, label: text(`invoice_filter_${value}`), count: data.invoices.filter((invoice) => matches(invoice, value)).length }))} />}>
      {shown.length === 0 ? <p className="xo-cust-quiet">{text('no_invoices_here')}</p>
        : <InvoiceRows invoices={shown} mayBill={data.mayBill} withSubmission onOpenSubmission={onOpenSubmission} onChanged={onChanged} />}
    </Surface>
  </div>;
}

// ------------------------------------------------------------------------------- accounts

// The address an email carries, shown once to whoever sent it, to pass on themselves if need be.
function SentLink({ url, children }) {
  const [copied, setCopied] = useState(false);
  return <div className="xo-cust-link" role="status">
    {children}
    <div><input value={url} readOnly onFocus={(event) => event.target.select()} aria-label={text('account_link')} />
      <button type="button" className="xo-btn-2" onClick={() => navigator.clipboard?.writeText(url).then(() => setCopied(true)).catch(() => {})}>
        <Copy aria-hidden="true" />{text(copied ? 'copied' : 'copy')}</button></div>
  </div>;
}

// A new account in their portal: their email, and we send them a link to set it up.
function AddAccountModal({ data, onClose, onDone }) {
  const [email, setEmail] = useState(''), [made, setMade] = useState(null);
  const { busy, error, run } = useBusy();
  async function submit(event) {
    event.preventDefault();
    if (made) { onClose(); return; }
    const result = await run(() => sendCustomerLink(data.customer.id, { email }));
    if (result) { setMade(result); onDone(); }
  }
  return <FormModal id="xo-add-account" title={text('add_account')} subtitle={data.customer.name} width="narrow" busy={busy} onClose={onClose} onSubmit={submit} cancel={!made}
    submitLabel={made ? text('done') : text(busy ? 'sending' : 'add_account_send')} submitDisabled={!made && email.trim() === ''}
    note={made ? '' : text('add_account_note')}>
    <FormSection title={text('their_email')} detail={text('add_account_hint_projects')}>
      <Field id="xo-account-email" label={text('link_email')}>
        <input {...fieldAria('xo-account-email')} type="email" value={email} onChange={(event) => setEmail(event.target.value)} maxLength={320} disabled={busy || made !== null} autoFocus />
      </Field>
      {made && <SentLink url={made.url}><p>{text(made.emailed ? 'account_link_emailed' : 'account_link_not_emailed', { date: day(made.expiresAt) })}</p></SentLink>}
    </FormSection>
    {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
  </FormModal>;
}

// The accounts in their portal: everyone of theirs who signs in sees the same portal and can send
// the samples of a submission; and those we sent a link who have not set it up yet.
function AccountsTab({ data, onAdd }) {
  const ready = data.customer.pointOfContact !== null && data.customer.isActive;
  return <div className="xo-cust-stack">
    {data.mayBill && !ready && <p className="xo-alert t-warn">{data.customer.isActive ? text('needs_contact_to_ask') : text('inactive_no_submissions')}</p>}
    <Surface title={text('accounts_count', { count: data.accounts.length })} detail={text('accounts_detail_projects')}
      action={data.mayBill && <button type="button" className="xo-btn" onClick={onAdd} disabled={!ready}><Plus aria-hidden="true" />{text('add_account')}</button>}>
      {data.accounts.length === 0 ? <p className="xo-cust-quiet">{text('no_accounts')}</p>
        : <ol className="xo-cust-rows">{data.accounts.map((account) => <li key={account.uuid}><div className="xo-cust-person-row">
          <span className="xo-cust-avatar" aria-hidden="true">{initials(account.name) || <UserRound />}</span>
          <span className="xo-cust-cell"><strong>{account.name}</strong><small>{account.email}</small></span>
          <span className="xo-cust-cell"><small>{account.lastSignedInAt ? text('last_signed_in', { when: labWhen(account.lastSignedInAt) }) : text('never_signed_in')}</small></span>
        </div></li>)}</ol>}
    </Surface>
    {data.pendingAccounts.length > 0 && <Surface title={text('accounts_pending')} detail={text('accounts_pending_detail')}>
      <ol className="xo-cust-rows">{data.pendingAccounts.map((pending) => <li key={pending.email}><div className="xo-cust-person-row">
        <span className="xo-cust-avatar is-pending" aria-hidden="true"><Send /></span>
        <span className="xo-cust-cell"><strong>{pending.email}</strong>
          <small>{pending.expiresAt > Date.now() ? text('link_sent_on', { date: day(pending.sentAt), until: day(pending.expiresAt) }) : text('link_ran_out')}</small></span>
        <span className="xo-cust-cell" />
      </div></li>)}</ol>
    </Surface>}
  </div>;
}

// ------------------------------------------------------------------------------- details

// Our point of contact for them (who hears of the samples they send), their billing address and tax number.
function BillingForm({ data, options, onSaved }) {
  const { customer } = data;
  const [fields, setFields] = useState({ contact: customer.pointOfContact ? String(customer.pointOfContact.id) : '',
    billingAddress: customer.billingAddress || '', taxNumber: customer.taxNumber || '' });
  const { busy, error, run } = useBusy();
  const set = (key) => (event) => setFields((current) => ({ ...current, [key]: event.target.value }));
  async function submit(event) {
    event.preventDefault();
    const saved = await run(() => saveCustomerDetails(customer.id, { pointOfContactAccountId: fields.contact === '' ? null : Number(fields.contact),
      billingAddress: fields.billingAddress, taxNumber: fields.taxNumber }));
    if (saved) onSaved();
  }
  return <form className="xo-cust-billing" onSubmit={submit}>
    <Field id="xo-billing-contact" label={text('point_of_contact')} hint={text('point_of_contact_told')}>
      <XomicsSelect id="xo-billing-contact" value={fields.contact} onChange={set('contact')} disabled={busy || !data.mayManage}>
        <option value="">{text('no_contact')}</option>
        {options.staff.map((member) => <option key={member.id} value={member.id}>{member.name} · {member.email}</option>)}
      </XomicsSelect>
    </Field>
    <Field id="xo-billing-address" label={text('billing_address')}>
      <textarea {...fieldAria('xo-billing-address')} value={fields.billingAddress} onChange={set('billingAddress')} rows={3} maxLength={500} disabled={busy || !data.mayManage} />
    </Field>
    <Field id="xo-billing-tax" label={text('tax_number')}>
      <input {...fieldAria('xo-billing-tax')} value={fields.taxNumber} onChange={set('taxNumber')} maxLength={64} disabled={busy || !data.mayManage} />
    </Field>
    {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
    {data.mayManage && <div className="xo-cust-form-actions"><button type="submit" className="xo-btn" disabled={busy}>{text(busy ? 'saving' : 'save')}</button></div>}
  </form>;
}

function DetailsTab({ data, organization, options, onEdit, onSaved }) {
  const details = organization.data?.organization;
  return <div className="xo-cust-columns">
    <section className="xo-pm-card">
      <div className="xo-pm-section-head"><div><h3>{text('the_organization')}</h3><p>{text('organization_detail')}</p></div>
        {data.mayManage && details && <button type="button" className="xo-btn-2" onClick={onEdit}>{org('edit')}</button>}</div>
      {organization.error ? <Failed message={organization.error} onRetry={organization.refresh} /> : !details ? <Loading label={text('loading')} /> : <>
        <dl className="xo-cust-facts">
          <div><dt>{org('name')}</dt><dd>{details.name}</dd></div>
          <div><dt>{org('code')}</dt><dd><code className="xo-pm-code">{details.code}</code></dd></div>
          <div><dt>{org('type')}</dt><dd>{details.type.label}</dd></div>
          <div><dt>{org('country')}</dt><dd>{details.countryCode}</dd></div>
          <div><dt>{text('their_contact')}</dt><dd>{details.contact.name}<small>{details.contact.email}{details.contact.phone && ` · ${details.contact.phone}`}</small></dd></div>
          <div><dt>{text('their_samples_go')}</dt><dd>{data.subProject ? <><code className="xo-pm-code">{data.subProject.code}</code><small>{text('under_customer_project')}</small></> : '—'}</dd></div>
          <div><dt>{org('created')}</dt><dd>{labWhen(details.createdAt)}</dd></div>
        </dl>
      </>}
    </section>
    <section className="xo-pm-card">
      <div className="xo-pm-section-head"><div><h3>{text('contact_and_billing')}</h3><p>{text('billing_detail')}</p></div></div>
      <BillingForm key={`${data.customer.id}-${data.customer.pointOfContact?.id ?? ''}`} data={data} options={options} onSaved={onSaved} />
    </section>
  </div>;
}

// ------------------------------------------------------------------------------- our billing profiles

const EMPTY_ISSUER = { legalName: '', registrationNumber: '', vatNumber: '', addressLine1: '', addressLine2: '', postalCode: '', city: '', region: '',
  countryCode: '', email: '', phone: '', website: '', paymentTerms: '', invoiceNote: '' };
const accountKey = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

// One billing profile, new (profile null) or changed: a company our invoices are issued by, as every
// invoice it issues shows it, and where customers pay it in each currency. An invoice keeps the
// details it was issued with; saving changes the ones issued from now on. One no longer in use can
// no longer be chosen for an invoice.
export function BillingProfileModal({ profile, currencies, onClose, onSaved }) {
  const [issuer, setIssuer] = useState(() => (profile ? Object.fromEntries(Object.keys(EMPTY_ISSUER).map((key) => [key, profile[key] ?? ''])) : EMPTY_ISSUER));
  const [accounts, setAccounts] = useState(() => (profile?.bankAccounts ?? []).map((account) => ({ key: accountKey(), ...account,
    iban: account.iban ?? '', bic: account.bic ?? '', accountNumber: account.accountNumber ?? '' })));
  const [inUse, setInUse] = useState(profile ? profile.isActive : true);
  const { busy, error, run } = useBusy();
  const set = (key) => (event) => setIssuer((current) => ({ ...current, [key]: event.target.value }));
  const setAccount = (key, field) => (event) => setAccounts((current) => current.map((account) => (account.key === key ? { ...account, [field]: event.target.value } : account)));
  const used = new Set(accounts.map((account) => account.currency));
  const free = currencies.filter((currency) => !used.has(currency.code));
  async function submit(event) {
    event.preventDefault();
    const saved = await run(() => saveBillingProfile(profile ? profile.id : null, {
      issuer,
      bankAccounts: accounts.map(({ key, ...account }) => ({ ...account, iban: account.iban || null, bic: account.bic || null, accountNumber: account.accountNumber || null })),
      isActive: inUse,
    }));
    if (saved) onSaved(saved);
  }
  const field = (key, label, props = {}) => <Field id={`bd-${key}`} label={label} hint={props.hint}>
    <input {...fieldAria(`bd-${key}`, { hint: Boolean(props.hint) })} value={issuer[key]} onChange={set(key)} maxLength={props.max ?? 255} disabled={busy}
      type={props.type ?? 'text'} />
  </Field>;
  return <FormModal id="xo-billing" title={profile ? profile.legalName : text('new_profile')} subtitle={text('billing_subtitle')} busy={busy}
    onClose={onClose} onSubmit={submit} submitLabel={text(busy ? 'saving' : 'save')} note={text('billing_note')}>
    <>
      {profile && <Choice title={text('profile_in_use')} detail={text('profile_in_use_hint')} on={inUse} onChange={setInUse} disabled={busy} />}
      <FormSection number="1" title={text('billing_company')} detail={text('billing_company_detail')}>
        <div className="xo-product-form-grid">
          <div className="xo-product-field-wide">{field('legalName', text('legal_name'))}</div>
          {field('registrationNumber', text('registration_number'), { max: 64 })}
          {field('vatNumber', text('vat_number'), { max: 64 })}
        </div>
      </FormSection>
      <FormSection number="2" title={text('billing_address_title')}>
        <div className="xo-product-form-grid">
          <div className="xo-product-field-wide">{field('addressLine1', text('address_line1'))}</div>
          <div className="xo-product-field-wide">{field('addressLine2', text('address_line2'))}</div>
          {field('postalCode', text('postal_code'), { max: 32 })}
          {field('city', text('city'), { max: 128 })}
          {field('region', text('region'), { max: 128 })}
          {field('countryCode', org('country'), { max: 2, hint: org('country_hint') })}
        </div>
      </FormSection>
      <FormSection number="3" title={text('billing_reach')}>
        <div className="xo-product-form-grid">
          {field('email', text('billing_email'), { type: 'email', max: 320 })}
          {field('phone', text('billing_phone'), { max: 64 })}
          <div className="xo-product-field-wide">{field('website', text('billing_website'))}</div>
        </div>
      </FormSection>
      <FormSection number="4" title={text('bank_accounts')} detail={text('bank_accounts_detail')}>
        {accounts.length === 0 && <p className="xo-cust-quiet">{text('no_bank_accounts')}</p>}
        {accounts.map((account) => <div className="xo-cust-bank" key={account.key}>
          <div className="xo-cust-bank-head"><strong>{account.currency}</strong>
            <button type="button" className="xo-icon-btn" aria-label={text('remove_account', { currency: account.currency })} disabled={busy}
              onClick={() => setAccounts((current) => current.filter((entry) => entry.key !== account.key))}><Trash2 aria-hidden="true" /></button></div>
          <div className="xo-product-form-grid">
            {[['bankName', text('bank_name'), 255], ['accountHolder', text('account_holder'), 255], ['iban', text('iban'), 42], ['bic', text('bic'), 11],
              ['accountNumber', text('account_number'), 64]].map(([key, label, max]) => <Field key={key} id={`bd-${account.key}-${key}`} label={label}>
              <input {...fieldAria(`bd-${account.key}-${key}`)} value={account[key]} onChange={setAccount(account.key, key)} maxLength={max} disabled={busy} />
            </Field>)}
          </div>
        </div>)}
        {free.length > 0 && <div className="xo-cust-add-bank">
          <XomicsSelect value="" disabled={busy} aria-label={text('add_bank_account')}
            onChange={(event) => { const code = event.target.value; if (code) setAccounts((current) => [...current, { key: accountKey(), currency: code, bankName: '', accountHolder: issuer.legalName, iban: '', bic: '', accountNumber: '' }]); }}>
            <option value="">{text('add_bank_account')}</option>
            {free.map((currency) => <option key={currency.code} value={currency.code}>{currency.code} · {currency.name}</option>)}
          </XomicsSelect>
        </div>}
      </FormSection>
      <FormSection number="5" title={text('on_every_invoice')}>
        <div className="xo-product-form-grid">
          <div className="xo-product-field-wide">{field('paymentTerms', text('payment_terms'), { max: 500, hint: text('payment_terms_hint') })}</div>
          <div className="xo-product-field-wide"><Field id="bd-invoiceNote" label={text('invoice_note')} hint={text('invoice_note_hint')}>
            <textarea {...fieldAria('bd-invoiceNote', { hint: true })} rows={3} value={issuer.invoiceNote} onChange={set('invoiceNote')} maxLength={1000} disabled={busy} />
          </Field></div>
        </div>
      </FormSection>
      {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
    </>
  </FormModal>;
}

// ------------------------------------------------------------------------------- the page

export default function CustomerPage({ id, types, options, onBack, onChanged }) {
  const load = useCallback(() => loadCustomer(id), [id]);
  const resource = useLiveResource(['organizations'], load);
  const loadDetails = useCallback(() => loadOrganization(id), [id]);
  const organization = useLiveResource(['organizations'], loadDetails);
  const [tab, setTab] = useState('projects'), [sheet, setSheet] = useState(null), [form, setForm] = useState(null), [made, setMade] = useState(null);
  const data = resource.data;
  // The page opens at its top.
  useEffect(() => { window.scrollTo?.(0, 0); }, [id]);
  const changed = () => { resource.refresh(); organization.refresh(); onChanged(); };
  if (resource.error) return <div className="xo-cust-page"><Back onBack={onBack} /><Failed message={resource.error} onRetry={resource.refresh} /></div>;
  if (data === null || options === null) return <div className="xo-cust-page"><Back onBack={onBack} /><Loading label={text('loading')} /></div>;
  const { customer } = data;
  const ready = customer.pointOfContact !== null && customer.isActive;
  const unpaid = totalsOf(data.invoices).filter((entry) => entry.open > 0);
  const overdue = data.invoices.filter((invoice) => invoice.status === 'overdue').length;
  const waiting = data.submissions.filter((submission) => submission.stage === 'requested').length;
  // At work: one-time projects whose samples are on their way or in the lab, and continuous ones open.
  const working = data.submissions.filter((submission) => ['sent', 'in_lab', 'open'].includes(submission.stage)).length;
  return <div className="xo-cust-page">
    <header className="xo-cust-head">
      <Back onBack={onBack} />
      <span className="xo-cust-initials" aria-hidden="true">{initials(customer.name)}</span>
      <div className="xo-cust-title">
        <h1>{customer.name}</h1>
        <p><code className="xo-pm-code">{customer.code}</code><span>{customer.type}</span><span>{customer.countryCode}</span>
          <Pill tone={customer.isActive ? 'ok' : 'flat'}>{org(customer.isActive ? 'is_active' : 'is_inactive')}</Pill></p>
      </div>
      {data.mayBill && <div className="xo-head-actions">
        <button type="button" className="xo-btn" onClick={() => setForm('submission')} disabled={!ready}
          title={ready ? undefined : text(customer.isActive ? 'needs_contact_to_ask' : 'inactive_no_submissions')}><Plus aria-hidden="true" />{text('new_project')}</button>
      </div>}
    </header>
    <section className="xo-transaction-window xo-cust-window">
      <div className="xo-transaction-window-total">
        <span>{text('figure_unpaid')}</span>
        {unpaid.length === 0 ? <strong>0</strong> : <Total totals={unpaid} field="open" as="strong" />}
        <small className={overdue > 0 ? 't-bad' : ''}>{overdue > 0 ? text('overdue_count', { count: overdue })
          : unpaid.length > 0 ? counted('open_invoices_count', data.invoices.filter(toPay).length)
            : text('nothing_to_pay')}</small>
      </div>
      <dl>
        <div><dt>{text('figure_waiting')}</dt><dd>{waiting}</dd><small>{text('figure_waiting_detail')}</small></div>
        <div><dt>{text('figure_working')}</dt><dd>{working}</dd><small>{text('figure_working_projects')}</small></div>
        <div><dt>{text('figure_contact')}</dt><dd className="is-name">{customer.pointOfContact ? customer.pointOfContact.name : '—'}</dd>
          <small>{customer.pointOfContact ? customer.pointOfContact.email : text('no_contact_yet')}</small></div>
      </dl>
    </section>
    {data.mayBill && !ready && <p className="xo-alert t-warn">{customer.isActive ? text('needs_contact_to_ask') : text('inactive_no_submissions')}</p>}
    {made && <div className="xo-cust-made" role="status">
      <Check aria-hidden="true" />
      <p><strong>{made.invoice ? text(made.invoice.kind === 'proforma' ? 'project_made_proforma' : 'project_made_invoiced', {
        number: made.submission.number, workflow: made.submission.workflow.name, invoice: made.invoice.number,
        amount: money(made.invoice.amountMinor, made.invoice.currency, made.invoice.minorUnit), date: made.invoice.dueAt === null ? '' : day(made.invoice.dueAt) })
        : text(made.submission.kind === 'continuous' ? 'project_made_continuous' : 'project_made',
          { number: made.submission.number, workflow: made.submission.workflow.name })}</strong>{toldLine(made, customer.name)}</p>
      <button type="button" className="xo-btn-2" onClick={() => setSheet(made.submission.uuid)}>{text('open_submission')}</button>
      <button type="button" className="xo-icon-btn" aria-label={text('close')} onClick={() => setMade(null)}><X aria-hidden="true" /></button>
    </div>}
    <WorkspaceTabs className="xo-cust-tabs" value={tab} onChange={setTab} label={text('sections')}
      tabs={Object.entries(TABS).map(([value, Icon]) => ({ value, Icon, label: text(`tab.${value}`),
        count: { projects: data.submissions.length, invoices: data.invoices.length, accounts: data.accounts.length }[value] }))} />
    {tab === 'projects' && <ProjectsTab data={data} onOpen={setSheet} onNew={() => setForm('submission')} />}
    {tab === 'invoices' && <InvoicesTab data={data} onOpenSubmission={setSheet} onChanged={changed} />}
    {tab === 'accounts' && <AccountsTab data={data} onAdd={() => setForm('account')} />}
    {tab === 'details' && <DetailsTab data={data} organization={organization} options={options} onEdit={() => setForm('organization')} onSaved={changed} />}

    {sheet !== null && <SubmissionSheet uuid={sheet} customerName={customer.name} mayBill={data.mayBill} billingProfiles={data.billingProfiles}
      onClose={() => setSheet(null)} onChanged={changed} />}
    {form === 'submission' && <NewSubmissionModal data={data} options={options} onClose={() => setForm(null)}
      onDone={(result) => { setForm(null); setMade(result); setTab('projects'); changed(); }} />}
    {form === 'account' && <AddAccountModal data={data} onClose={() => setForm(null)} onDone={changed} />}
    {form === 'organization' && organization.data && <OrganizationForm organization={organization.data.organization} types={types}
      onCancel={() => setForm(null)} onSaved={() => { setForm(null); changed(); }} />}
  </div>;
}

// Back to the list, as the portal goes back: a chevron at the head's left edge, its destination
// named for screen readers.
function Back({ onBack }) {
  return <button type="button" className="xo-cust-back" onClick={onBack} aria-label={text('all_customers')} title={text('all_customers')}>
    <ChevronLeft aria-hidden="true" /></button>;
}
